import React from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from './Button';
import { cn } from '../../utils/cn';

interface ThemeToggleProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'button' | 'dropdown';
}

export const ThemeToggle: React.FC<ThemeToggleProps> = ({ 
  className,
  size = 'md',
  variant = 'button'
}) => {
  const { theme, actualTheme, setTheme, toggleTheme } = useTheme();

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className="h-4 w-4" aria-hidden="true" />;
      case 'dark':
        return <Moon className="h-4 w-4" aria-hidden="true" />;
      case 'system':
        return <Monitor className="h-4 w-4" aria-hidden="true" />;
      default:
        return <Sun className="h-4 w-4" aria-hidden="true" />;
    }
  };

  const getThemeLabel = () => {
    switch (theme) {
      case 'light':
        return 'Light mode';
      case 'dark':
        return 'Dark mode';
      case 'system':
        return 'System preference';
      default:
        return 'Light mode';
    }
  };

  const getNextThemeLabel = () => {
    switch (theme) {
      case 'light':
        return 'Switch to dark mode';
      case 'dark':
        return 'Switch to system preference';
      case 'system':
        return 'Switch to light mode';
      default:
        return 'Switch to dark mode';
    }
  };

  if (variant === 'dropdown') {
    return (
      <div className={cn('relative', className)}>
        <div className="flex flex-col space-y-1 p-2">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Theme
          </span>
          {(['light', 'dark', 'system'] as const).map((themeOption) => (
            <button
              key={themeOption}
              onClick={() => setTheme(themeOption)}
              className={cn(
                'flex items-center space-x-2 px-3 py-2 text-sm rounded-md transition-colors duration-200',
                'hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500',
                theme === themeOption
                  ? 'bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                  : 'text-gray-700 dark:text-gray-300'
              )}
              aria-pressed={theme === themeOption}
            >
              {themeOption === 'light' && <Sun className="h-4 w-4" aria-hidden="true" />}
              {themeOption === 'dark' && <Moon className="h-4 w-4" aria-hidden="true" />}
              {themeOption === 'system' && <Monitor className="h-4 w-4" aria-hidden="true" />}
              <span className="capitalize">{themeOption}</span>
              {themeOption === 'system' && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  ({actualTheme})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      size={size}
      onClick={toggleTheme}
      className={cn(
        'relative p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400',
        'hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200',
        className
      )}
      aria-label={getNextThemeLabel()}
      title={`Current: ${getThemeLabel()}. Click to ${getNextThemeLabel().toLowerCase()}`}
    >
      <div className="relative">
        {getThemeIcon()}
        
        {/* Visual indicator for system theme */}
        {theme === 'system' && (
          <div className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-primary-500 rounded-full border border-white dark:border-gray-900" />
        )}
      </div>
    </Button>
  );
};

export default ThemeToggle;